import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Wallet, User } from 'lucide-react';
import { formatTSh } from '@/lib/currency';
import PaymentMethodSelector from '@/components/PaymentMethodSelector';

const PublicRecharge: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [linkData, setLinkData] = useState<any>(null);
  const [amount, setAmount] = useState('');
  const [meterId, setMeterId] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      toast({
        variant: 'destructive',
        title: t('publicRecharge.invalidLink'),
        description: t('publicRecharge.invalidLinkDesc'),
      });
      navigate('/preauth');
      return;
    }

    const fetchLinkData = async () => {
      try {
        const { data, error } = await supabase
          .from('recharge_links')
          .select('*')
          .eq('token', token)
          .eq('is_active', true)
          .single();

        if (error || !data) {
          toast({
            variant: 'destructive',
            title: t('publicRecharge.linkNotFound'),
            description: t('publicRecharge.linkNotFoundDesc'),
          });
          navigate('/preauth');
          return;
        }

        // Check if link is expired
        if (new Date(data.expires_at) < new Date()) {
          toast({
            variant: 'destructive',
            title: t('publicRecharge.linkExpired'),
            description: t('publicRecharge.linkExpiredDesc'),
          });
          navigate('/preauth');
          return;
        }

        setLinkData(data);

        // Use meter_id stored directly on the recharge link
        if (data.meter_id) {
          setMeterId(data.meter_id);
        }
      } catch (error) {
        console.error('Error fetching recharge link:', error);
        toast({
          variant: 'destructive',
          title: t('common.errorTitle'),
          description: t('publicRecharge.loadFailed'),
        });
      } finally {
        setLoading(false);
      }
    };

    fetchLinkData();
  }, [token, navigate, toast]);

  const handlePaymentSuccess = async (data: any) => {
    // Update usage count when payment is initiated
    try {
      await supabase
        .from('recharge_links')
        .update({ used_count: (linkData.used_count || 0) + 1 })
        .eq('id', linkData.id);
    } catch (error) {
      console.error('Failed to update usage count:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-blue-50 dark:from-gray-900 dark:to-gray-800">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-green-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!linkData) {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 p-4">
      <div className="w-full max-w-sm sm:max-w-3xl">
      <Card className="w-full">
        <CardHeader className="text-center space-y-2">
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-2">
            <Wallet className="w-8 h-8 text-green-600 dark:text-green-400" />
          </div>
          <CardTitle className="text-2xl">{t('publicRecharge.rechargeAccount')}</CardTitle>
          <p className="text-muted-foreground">
            {t('publicRecharge.helpTopUpFor')}
          </p>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Client Info */}
          <div className="bg-muted rounded-lg p-4 space-y-2">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-muted-foreground" />
              <span className="font-medium">{linkData.client_name}</span>
            </div>
            {linkData.client_number && (
              <p className="text-sm text-muted-foreground">
                {t('publicRecharge.clientHash', { number: linkData.client_number })}
              </p>
            )}
          </div>

          {/* Amount Input */}
          <div className="space-y-2">
            <Label htmlFor="amount">{t('publicRecharge.amountTSh')}</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">
                TSh
              </span>
              <Input
                id="amount"
                type="number"
                inputMode="numeric"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="pl-12"
                placeholder={t('publicRecharge.enterAmount')}
                min="100"
              />
            </div>
            {amount && parseFloat(amount) > 0 && (
              <p className="text-sm text-muted-foreground">
                {t('publicRecharge.aboutToRecharge', { amount: formatTSh(parseFloat(amount)) })}
              </p>
            )}
          </div>

          {/* Quick Amounts */}
          <div className="space-y-2">
            <Label>{t('publicRecharge.quickAmounts')}</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {[10000, 20000, 50000].map((quickAmount) => (
                <Button
                  key={quickAmount}
                  variant="outline"
                  size="sm"
                  onClick={() => setAmount(quickAmount.toString())}
                  className={amount === quickAmount.toString() ? 'border-green-500 bg-green-50 dark:bg-green-900/20' : ''}
                >
                  {formatTSh(quickAmount)}
                </Button>
              ))}
            </div>
          </div>

          {/* Payment Methods */}
          {amount && parseFloat(amount) > 0 && (
            <div className="pt-4">
              <PaymentMethodSelector
                amount={parseFloat(amount)}
                userId={linkData.user_id}
                meterId={meterId || undefined}
                source="shared_link"
                onSuccess={handlePaymentSuccess}
              />
            </div>
          )}

          {/* Usage Info */}
          <div className="text-center text-xs text-muted-foreground pt-4 border-t">
            <p>{t('publicRecharge.usedTimes', { count: linkData.used_count || 0 })}</p>
            <p className="mt-1">
              {t('publicRecharge.expiresOn', { date: new Date(linkData.expires_at).toLocaleDateString() })}
            </p>
          </div>
        </CardContent>
      </Card>
      </div>
    </div>
  );
};

export default PublicRecharge;